import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, Check, Copy, Link2, Linkedin, LoaderCircle } from 'lucide-react'
import { PageShell } from '@/features/public-site/page-shell'
import { appFetch } from '@/shared/api/app-client'
import type { ApiResponse } from '@/shared/api/app-types'
import './blog-article.css'

export const Route = createFileRoute('/announcements/$announcementId')({ component: AnnouncementDetailPage })

type AnnouncementDetail = {
  id: number
  title: string
  body: string
  contentHtml: string | null
  excerpt: string
  coverImageUrl: string | null
  category: string
  authorName: string
  actionLabel: string | null
  actionUrl: string | null
  publishedAt: string
}

function AnnouncementDetailPage() {
  const { announcementId } = Route.useParams()
  const [item, setItem] = useState<AnnouncementDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setLoading(true)
    setError('')
    void appFetch<ApiResponse<AnnouncementDetail>>(`/api/announcements/${announcementId}`)
      .then((response) => setItem(response.data))
      .catch((requestError) => setError(requestError instanceof Error ? requestError.message : '文章加载失败，请稍后重试。'))
      .finally(() => setLoading(false))
  }, [announcementId])

  const tableOfContents = useMemo(() => {
    if (!item?.contentHtml) return []
    return [...item.contentHtml.matchAll(/<h([2-3])[^>]*>([\s\S]*?)<\/h\1>/gi)]
      .map((match) => (match[2] ?? '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim())
      .filter(Boolean)
      .slice(0, 8)
  }, [item?.contentHtml])

  const copyLink = async () => {
    await navigator.clipboard?.writeText(window.location.href)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <PageShell>
      <div className="min-h-[720px] bg-white px-5 pb-24 pt-8 sm:px-8 sm:pt-12">
        <main className="mx-auto max-w-[1216px]">
          <Link to="/announcements" className="inline-flex items-center gap-2 text-sm font-medium text-[#596274] transition-colors hover:text-[#3f9437]"><ArrowLeft className="size-4" />返回博客</Link>
          {loading ? <div className="flex items-center justify-center gap-2 py-32 text-sm text-[#697184]"><LoaderCircle className="size-4 animate-spin" />正在加载文章…</div> : null}
          {error ? <div className="mt-8 rounded-2xl border border-red-200 bg-white px-6 py-12 text-center text-sm text-red-700">{error}</div> : null}
          {!loading && !error && item ? (
            <article className="article-detail mt-11">
              <header className="article-detail__hero">
                <div className="article-detail__intro">
                  <p className="article-detail__category">{item.category}</p>
                  <h1>{item.title}</h1>
                  <p className="article-detail__excerpt">{item.excerpt}</p>
                  <time className="article-detail__date" dateTime={item.publishedAt}>{new Date(item.publishedAt).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}</time>
                  <div className="article-detail__author"><span>{item.authorName}</span><small>JIAN 博客</small></div>
                </div>
                <img src={item.coverImageUrl || '/assets/landing/ting.png'} alt={item.title} className="article-detail__cover" />
              </header>

              <section className="article-detail__summary" aria-label="文章摘要">
                <h2>文章摘要</h2>
                <p>{item.excerpt}</p>
              </section>

              <div className="article-detail__content-grid">
                <div className="article-detail__body">
                  {item.contentHtml ? <div className="blog-article" dangerouslySetInnerHTML={{ __html: item.contentHtml }} /> : <div className="blog-article whitespace-pre-wrap">{item.body}</div>}
                  {item.actionUrl ? <a href={item.actionUrl} className="article-detail__action">{item.actionLabel || '立即查看'}<ArrowRight className="size-4" /></a> : null}
                </div>
                <aside className="article-detail__aside">
                  <div className="article-detail__share"><h2>分享</h2><div><a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer" aria-label="分享到 LinkedIn"><Linkedin className="size-4" /></a><button type="button" onClick={() => void copyLink()} aria-label="复制文章链接">{copied ? <Check className="size-4 text-[#3f9437]" /> : <Copy className="size-4" />}</button><a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(item.title)}`} target="_blank" rel="noreferrer" aria-label="分享到 X"><Link2 className="size-4" /></a></div></div>
                  {tableOfContents.length ? <nav className="article-detail__toc" aria-label="本页目录"><h2>本页内容</h2><ol>{tableOfContents.map((heading) => <li key={heading}>{heading}</li>)}</ol></nav> : null}
                </aside>
              </div>
            </article>
          ) : null}
        </main>
      </div>
    </PageShell>
  )
}
